import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { ClientsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { ClientFilters, ClientOrder, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'
import { Button } from '../base/button'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'
import { Badge } from '@/components/client_type/badge'

interface Props extends TableProps {
  filters?: ClientFilters
  createable?: boolean
}

export const Table = ({ pageSize = 20, filters = {}, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<ClientsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.clients.pageInfo, onIndex, basePath: '/clients' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<ClientFilters, ClientOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ organisationName: SortOrder.Asc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.clients.nodes = dedupe([
              ...previousData.clients.nodes,
              ...fetchMoreResult.clients.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        headings={[
          { name: 'Name', sortField: 'organisationName', defaultDirection: SortOrder.Asc },
          { name: 'Client type' },
          { name: 'Events' },
        ]}
        rows={data.clients.nodes.map(client => (
          {
            id: client.id,
            columns: [
              client.organisationName,
              <Badge clientType={client.clientType} />,
              client.eventsCount
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
